import React, { useState, useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';

import { expValEquals } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';

import { ReadTimeComponent } from './ReadTimeComponent.tsx';
import type { ContentFeatures } from './featuresExtractor.ts';
import type { ReadTimePopupProps } from './ReadTimePopup.tsx';

const ReadTimePopup = LoadableLazy<ReadTimePopupProps>({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ReadTimePopup" */ '../src/ReadTimePopup.tsx'))
			.ReadTimePopup,
});

const triggerStyles = xcss({
	display: 'inline-flex',
	color: 'color.text',
	font: 'font.body.small',
	padding: 'space.0',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.neutral.subtle',
});

interface ReadTimePopupWrapperProps {
	contentId: string;
	isAbbreviatedReadTime?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	wordCount: number | null;
	readTime: number | null;
	source: string;
	isReadTimeCapped?: boolean;
}

export const ReadTimePopupWrapper = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
	features,
	featuresSkipExpand,
	wordCount,
	readTime,
	source,
	isReadTimeCapped,
}: ReadTimePopupWrapperProps): React.JSX.Element => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onPageReadTimeClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'readTime',
				actionSubjectId: 'readTimePopup',
				source,
				attributes: {
					contentId,
				},
			},
		}).fire();
		setIsPopupOpen(!isPopupOpen);
	}, [isPopupOpen, createAnalyticsEvent, contentId, source]);

	const shouldRenderToParent = useMemo(
		() => expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true),
		[],
	);

	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={() => setIsPopupOpen(false)}
			placement="bottom-start"
			shouldRenderToParent={shouldRenderToParent}
			content={(props) => (
				<ReadTimePopup
					{...props}
					features={features}
					featuresSkipExpand={featuresSkipExpand}
					source={source}
					contentId={contentId}
				/>
			)}
			trigger={(triggerProps) => (
				<Pressable {...triggerProps} onClick={onPageReadTimeClick} xcss={triggerStyles}>
					<ReadTimeComponent
						readTime={readTime}
						wordCount={wordCount}
						pageId={contentId}
						isAbbreviatedReadTime={isAbbreviatedReadTime}
						isDetailsPanel={isDetailsPanel}
						showWordCount={showWordCount}
						isReadTimeCapped={isReadTimeCapped}
					/>
				</Pressable>
			)}
		/>
	);
};
